// Beamer "What's New" widget loader. Mirrors ServiceAI's beamer-init.js + embed
// pattern (ServiceAI.Client/wwwroot/js/beamer-init.js + index.html): the widget
// binds to the #beamer-button trigger in the header and renders its own unread
// badge offset to that element's top-right.
//
// Loaded from the AppShell effect rather than index.html so the third-party
// script only loads for authenticated users (the shell is the auth-gated chrome)
// and never on the public /login page.

/** Beamer product id for CloudRadial AutomationAI. */
const PRODUCT_ID = 'xcLbEsfi84504';
const EMBED_SRC = 'https://app.getbeamer.com/js/beamer-embed.js';
const EMBED_ID = 'beamer-embed-script';

interface BeamerConfig {
  product_id: string;
  selector: string;
  top?: number;
  right?: number;
  lazy?: boolean;
  notification_prompt?: string;
}

interface BeamerApi {
  init: () => void;
  /** Removes the widget + all bindings/DOM Beamer injected (used on logout). */
  destroy?: () => void;
}

declare global {
  interface Window {
    beamer_config?: BeamerConfig;
    Beamer?: BeamerApi;
  }
}

/**
 * Configure Beamer and ensure the embed script is loaded. Safe to call on every
 * shell mount: the config is (re)set each time, the <script> is injected once,
 * and if Beamer is already present we re-init so it (re)binds to the trigger.
 * The #beamer-button element is committed to the DOM by React before this effect
 * runs, so Beamer's selector binding always finds it.
 *
 * Returns a disposer (suitable as a React effect cleanup) that tears Beamer down
 * — without it, signing out and landing on /login leaves Beamer's injected
 * widget/state resident in the tab.
 */
export function loadBeamer(): () => void {
  if (typeof window === 'undefined') return () => {};

  window.beamer_config = {
    product_id: PRODUCT_ID,
    selector: '#beamer-button',
    // Offsets position the unread-count badge over the trigger.
    top: 0,
    right: 0,
    lazy: true,
    notification_prompt: 'sidebar',
  };

  if (document.getElementById(EMBED_ID)) {
    window.Beamer?.init();
    return unloadBeamer;
  }

  const script = document.createElement('script');
  script.id = EMBED_ID;
  script.src = EMBED_SRC;
  script.defer = true;
  // Beamer's SPA embed does NOT auto-bind the trigger — it must be init()-ed
  // once the script has defined window.Beamer (ServiceAI does this on
  // DOMContentLoaded). Without this the #beamer-button click never opens the
  // panel. onload fires after the script has executed, so window.Beamer exists.
  script.onload = () => {
    window.Beamer?.init();
  };
  document.body.appendChild(script);
  return unloadBeamer;
}

/**
 * Tear Beamer down: remove its injected widget/bindings (Beamer.destroy), drop
 * the embed <script>, and clear the global config. Called as the AppShell
 * effect's cleanup on unmount/logout.
 */
export function unloadBeamer(): void {
  if (typeof window === 'undefined') return;
  window.Beamer?.destroy?.();
  document.getElementById(EMBED_ID)?.remove();
  delete window.beamer_config;
}
